import { router, useGlobalSearchParams, usePathname } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DeliveryClosedActionSheet } from 'components/delivery-closed-action-sheet';
import { useUpdateOnAppStateChange } from 'hooks/use-update-on-app-state-change';
import { useCRMEventsContext } from 'state/crm-events';
import { DialogViewComponentNames } from 'state/crm-events/constants';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { getDateNowByTimezone, isRestaurantOpen } from 'utils/restaurant';
import { isCartRoute, isItemPickerOrComboRoute, routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';

function isRewardsPage(pathname: string | undefined, section?: string | string[]) {
  if (!pathname) {
    return false;
  }

  const sectionValue = Array.isArray(section) ? section[0] : section;
  return (
    pathname.startsWith('/rewards') ||
    (pathname.startsWith(routes.offersV2) && sectionValue === 'rewards')
  );
}

function isDeliveryClosedModalRoute(pathname: string | undefined, section?: string | string[]) {
  if (!pathname || isRewardsPage(pathname, section)) {
    return false;
  }

  return (
    isCartRoute(pathname) ||
    pathname.startsWith('/v2/cart') ||
    pathname === routes.menu ||
    pathname.startsWith(`${routes.menu}/`) ||
    pathname.startsWith('/v2/menu') ||
    isItemPickerOrComboRoute(pathname) ||
    pathname.startsWith(routes.offers) ||
    pathname.startsWith(routes.offersV2)
  );
}

function formatDeliveryAddress(deliveryAddress: {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
}) {
  return [
    deliveryAddress.addressLine1,
    deliveryAddress.addressLine2,
    [deliveryAddress.city, deliveryAddress.state].filter(Boolean).join(', '),
    deliveryAddress.zip,
  ]
    .filter(Boolean)
    .join(', ');
}

export function useDeliveryClosedDialog() {
  const pathname = usePathname();
  const { section } = useGlobalSearchParams<{ section?: string }>();
  const { serviceMode } = useServiceModeContext();
  const { store, fetchStore, isFetchingStore, hasCalledSetStore } = useStoreContext();
  const { deliveryAddress } = useOrderContext();
  const { logDialogView } = useCRMEventsContext();
  const previousPathnameRef = useRef(pathname);
  const pathnameRef = useRef(pathname);
  const sectionRef = useRef(section);
  const serviceModeRef = useRef(serviceMode);
  const isNavigatingAwayRef = useRef(false);
  const [isDeliveryClosedDialogOpen, setIsDeliveryClosedDialogOpen] = useState(false);

  pathnameRef.current = pathname;
  sectionRef.current = section;
  serviceModeRef.current = serviceMode;

  const onRewardsPage = isRewardsPage(pathname, section);
  const onModalRoute = isDeliveryClosedModalRoute(pathname, section);
  const isDeliveryClosed =
    isDelivery(serviceMode) &&
    hasCalledSetStore &&
    !!store?.number &&
    !isRestaurantOpen(
      store.deliveryHours,
      getDateNowByTimezone({ timeZone: store.timezone || undefined })
    );

  const canShowDeliveryClosedDialog = () =>
    !isNavigatingAwayRef.current &&
    isDelivery(serviceModeRef.current) &&
    isDeliveryClosedModalRoute(pathnameRef.current, sectionRef.current);

  useEffect(() => {
    if (!onModalRoute) {
      isNavigatingAwayRef.current = false;
    }
  }, [onModalRoute]);

  useEffect(() => {
    if (onRewardsPage || !onModalRoute || isNavigatingAwayRef.current) {
      setIsDeliveryClosedDialogOpen(false);
      return;
    }
    setIsDeliveryClosedDialogOpen(isDeliveryClosed);
  }, [isDeliveryClosed, onModalRoute, onRewardsPage]);

  useEffect(() => {
    if (isDeliveryClosedDialogOpen) {
      logDialogView(DialogViewComponentNames.DELIVERY_STORE_CLOSED);
    }
  }, [isDeliveryClosedDialogOpen, logDialogView]);

  const refreshIfNeeded = useCallback(() => {
    if (!onModalRoute || !isDelivery(serviceMode) || !store?.number || isFetchingStore) {
      return;
    }

    void fetchStore(store.number)
      .then(fetchedStore => {
        if (!fetchedStore || !canShowDeliveryClosedDialog()) {
          return;
        }

        const isStoreClosed = !isRestaurantOpen(
          fetchedStore.deliveryHours,
          getDateNowByTimezone({ timeZone: fetchedStore.timezone || undefined })
        );
        setIsDeliveryClosedDialogOpen(isStoreClosed);
      })
      .catch(() => {});
  }, [fetchStore, isFetchingStore, onModalRoute, serviceMode, store?.number]);

  useEffect(() => {
    const pathnameChanged = previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;

    if (pathnameChanged) {
      refreshIfNeeded();
    }
  }, [pathname, refreshIfNeeded]);

  useUpdateOnAppStateChange(
    useCallback(
      appState => {
        if (appState === 'active') {
          refreshIfNeeded();
        }
      },
      [refreshIfNeeded]
    )
  );

  const handleChangeDeliveryAddress = () => {
    isNavigatingAwayRef.current = true;
    setIsDeliveryClosedDialogOpen(false);
    router.navigate(routes.address);
  };

  const handlePickUpElsewhere = () => {
    isNavigatingAwayRef.current = true;
    setIsDeliveryClosedDialogOpen(false);
    router.navigate(routes.serviceMode);
  };

  const address = formatDeliveryAddress(deliveryAddress);

  return (
    <DeliveryClosedActionSheet
      isOpen={isDeliveryClosedDialogOpen}
      address={address}
      onChangeDeliveryAddress={handleChangeDeliveryAddress}
      onPickUpElsewhere={handlePickUpElsewhere}
    />
  );
}
